// src/ToolDetail.jsx
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { db } from "./firebase";
import { collection, getDocs } from "firebase/firestore";
import Footer from "./Footer";
// SEO: Import SEO utilities for dynamic meta tags, analytics, and structured data
import {
  getToolTitle,
  getToolDescription,
  getDefaultTagline,
  updateMetaTags,
  getCanonicalUrl,
  trackPageView,
  trackOutboundClick,
  generateSlug,
  generateToolSchema,
  injectJsonLd,
  removeJsonLd,
  getCategoryDisplayName,
} from "./utils/seo";

export default function ToolDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [tool, setTool] = useState(null);
  const [allTools, setAllTools] = useState([]); // SEO: Store all tools for related section
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Fetch tool by slug (match against generated slug from name)
  useEffect(() => {
    async function fetchTool() {
      try {
        const toolsRef = collection(db, "tools");
        const snapshot = await getDocs(toolsRef);
        
        // SEO: Store all tools for related tools section
        const tools = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setAllTools(tools);

        // Find tool where slug matches
        const foundTool = tools.find((t) => {
          const toolSlug = t.slug || generateSlug(t.name);
          return toolSlug === slug;
        });

        if (foundTool) {
          setTool(foundTool);
        } else {
          setError("Tool not found");
        }
      } catch (err) {
        console.error("Error fetching tool:", err);
        setError("Could not load tool. Please try again.");
      } finally {
        setLoading(false);
      }
    }

    fetchTool();
  }, [slug]);

  // SEO: Update meta tags and inject JSON-LD structured data
  useEffect(() => {
    if (tool) {
      // SEO: Update page meta tags
      updateMetaTags({
        title: getToolTitle(tool),
        description: getToolDescription(tool),
        canonicalUrl: getCanonicalUrl(`/tool/${slug}`),
      });
      trackPageView(`/tool/${slug}`, getToolTitle(tool));

      // SEO: Inject JSON-LD structured data (SoftwareApplication schema)
      const schema = generateToolSchema(tool, slug);
      injectJsonLd(schema, "json-ld-tool");
    }

    // Cleanup: reset title and remove JSON-LD when leaving
    return () => {
      document.title = "Tolio – Discover AI Tools";
      removeJsonLd("json-ld-tool");
    };
  }, [tool, slug]);

  // SEO: Get related tools from the same category (max 5 for internal linking)
  const relatedTools = allTools
    .filter((t) => 
      t.id !== tool?.id && 
      t.category && 
      tool?.category && 
      t.category.toLowerCase() === tool.category.toLowerCase()
    )
    .slice(0, 5);

  // Pricing display helper
  const getPricingInfo = () => {
    if (typeof tool.isFree === "boolean") {
      return tool.isFree ? "Free" : "Paid / Trial";
    }
    return tool.pricing || "Pricing not specified";
  };

  const pricingColor =
    tool?.isFree === true
      ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
      : "bg-amber-500/20 text-amber-400 border-amber-500/30";

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-neutral-950 flex items-center justify-center">
        <p className="text-neutral-500 dark:text-neutral-400">Loading tool...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white dark:bg-neutral-950 flex flex-col items-center justify-center gap-4">
        <p className="text-red-500">{error}</p>
        <button
          onClick={() => navigate("/")}
          className="px-6 py-2 rounded-full bg-lime-500 text-black font-medium hover:bg-lime-400 transition"
        >
          ← Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-neutral-900 dark:bg-neutral-950 dark:text-neutral-50 transition-colors duration-300">
      {/* Breadcrumb */}
      <nav className="max-w-4xl mx-auto px-4 pt-6">
        <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
          <Link to="/" className="hover:text-lime-500 transition">
            Home
          </Link>
          <span>/</span>
          <Link
            to={`/?category=${encodeURIComponent(tool.category || "All")}`}
            className="hover:text-lime-500 transition"
          >
            {tool.category || "Tools"}
          </Link>
          <span>/</span>
          <span className="text-neutral-700 dark:text-neutral-200">{tool.name}</span>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-10">
        {/* SEO: Header Section with single H1 */}
        <header className="flex flex-col sm:flex-row items-start gap-6 mb-8">
          {/* Tool Icon */}
          <div
            className="
              text-6xl p-5 rounded-3xl
              bg-gradient-to-br from-white/20 to-white/5
              dark:from-white/10 dark:to-white/5
              border border-white/20 dark:border-white/10
              shadow-lg
            "
            role="img"
            aria-label={`${tool.name} icon`}
          >
            {tool.icon || "🧰"}
          </div>

          <div className="flex-1">
            {/* SEO: Single H1 with tool name only */}
            <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 dark:text-white mb-2">
              {tool.name}
            </h1>
            
            {/* SEO: Tagline as subtitle (not in H1) */}
            <p className="text-lg text-neutral-500 dark:text-neutral-400 mb-3">
              {tool.tagline || getDefaultTagline(tool.category)}
            </p>

            {/* Pricing Badge */}
            <span
              className={`
                inline-block px-4 py-1.5 rounded-full text-sm font-medium border
                ${pricingColor}
              `}
            >
              {getPricingInfo()}
            </span>
          </div>
        </header>

        {/* SEO: Discovery disclaimer - establishes page intent */}
        <div className="mb-8 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            ℹ️ This page provides an overview and discovery information for <strong>{tool.name}</strong>. 
            Visit the official website for complete details.
          </p>
        </div>

        {/* SEO: Section 1 - "What is {Tool}?" (On-page SEO structure) */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4 text-neutral-800 dark:text-neutral-100">
            What is {tool.name}?
          </h2>
          <div className="prose prose-neutral dark:prose-invert max-w-none">
            <p className="text-neutral-700 dark:text-neutral-300 text-lg leading-relaxed">
              {tool.description || `${tool.name} is a powerful ${getCategoryDisplayName(tool.category)} that helps users accomplish their goals more efficiently.`}
            </p>
            {tool.longDescription && (
              <p className="text-neutral-600 dark:text-neutral-400 mt-4 leading-relaxed">
                {tool.longDescription}
              </p>
            )}
          </div>
        </section>

        {/* SEO: Section 2 - "Key Features" (On-page SEO structure) */}
        <section className="mb-10 p-6 rounded-2xl bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
          <h2 className="text-xl font-semibold mb-4 text-neutral-800 dark:text-neutral-100">
            Key Features
          </h2>
          {tool.features && tool.features.length > 0 ? (
            <ul className="space-y-2">
              {tool.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-neutral-700 dark:text-neutral-300">
                  <span className="text-lime-500 mt-1">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
          ) : (
            <ul className="space-y-2 text-neutral-700 dark:text-neutral-300">
              <li className="flex items-start gap-2">
                <span className="text-lime-500 mt-1">✓</span>
                {getCategoryDisplayName(tool.category)} capabilities
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lime-500 mt-1">✓</span>
                Easy to use interface
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lime-500 mt-1">✓</span>
                Regular updates and improvements
              </li>
            </ul>
          )}
        </section>

        {/* SEO: Section 3 - "Pricing" (On-page SEO structure) */}
        <section className="mb-10 p-6 rounded-2xl bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
          <h2 className="text-xl font-semibold mb-4 text-neutral-800 dark:text-neutral-100">
            Pricing
          </h2>
          <div className="flex items-center gap-4">
            <span className={`inline-block px-4 py-2 rounded-full text-sm font-bold border ${pricingColor}`}>
              {getPricingInfo()}
            </span>
            <p className="text-neutral-600 dark:text-neutral-400">
              {tool.isFree === true 
                ? "This tool offers a free tier. Visit the official website for details."
                : tool.pricing || "Free trial or paid plans available. Visit the official website for current pricing."
              }
            </p>
          </div>
        </section>

        {/* SEO: Key Info Section - Category & Website */}
        <section className="mb-10 p-6 rounded-2xl bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
          <h2 className="text-lg font-semibold mb-4 text-neutral-800 dark:text-neutral-100">
            Tool Information
          </h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Category */}
            <div>
              <dt className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">
                Category
              </dt>
              <dd>
                <Link
                  to={`/?category=${encodeURIComponent(tool.category || "All")}`}
                  className="
                    inline-block px-3 py-1 rounded-full text-sm font-medium
                    bg-white dark:bg-neutral-800
                    text-neutral-700 dark:text-neutral-200
                    border border-neutral-300 dark:border-neutral-700
                    hover:border-lime-500 hover:text-lime-600 dark:hover:text-lime-400
                    transition
                  "
                >
                  {tool.category || "Uncategorized"}
                </Link>
              </dd>
            </div>

            {/* Pricing */}
            <div>
              <dt className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">
                Pricing
              </dt>
              <dd className="text-neutral-800 dark:text-neutral-200 font-medium">
                {getPricingInfo()}
              </dd>
            </div>

            {/* Official Website */}
            <div className="sm:col-span-2">
              <dt className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">
                Official Website
              </dt>
              <dd className="text-neutral-600 dark:text-neutral-400 text-sm break-all">
                {tool.url || tool.website || "Not available"}
              </dd>
            </div>
          </dl>
        </section>

        {/* Primary CTA Button */}
        <section className="flex flex-col sm:flex-row gap-4 items-center">
          <a
            href={tool.url || tool.website || "#"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackOutboundClick(tool.url || tool.website, tool.name)}
            className="
              inline-flex items-center gap-2 px-8 py-4 rounded-full
              bg-gradient-to-r from-lime-400 to-emerald-500
              text-black font-semibold text-lg
              shadow-lg shadow-lime-500/25
              hover:shadow-xl hover:shadow-lime-500/40
              hover:scale-105
              transition-all duration-300
            "
          >
            Visit Official Website →
          </a>

          <Link
            to="/"
            className="
              inline-flex items-center gap-2 px-6 py-3 rounded-full
              bg-neutral-200 dark:bg-neutral-800
              text-neutral-700 dark:text-neutral-300
              font-medium
              hover:bg-neutral-300 dark:hover:bg-neutral-700
              transition
            "
          >
            ← Back to All Tools
          </Link>
        </section>

        {/* SEO: Section 4 - "Similar Tools" (Internal linking for SEO authority) */}
        {relatedTools.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl font-semibold mb-6 text-neutral-800 dark:text-neutral-100">
              Similar {getCategoryDisplayName(tool.category)} Tools
            </h2>
            <p className="text-neutral-600 dark:text-neutral-400 mb-4">
              Looking for alternatives to {tool.name}? Check out these similar tools:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {relatedTools.map((relatedTool) => {
                const relatedSlug = relatedTool.slug || generateSlug(relatedTool.name);
                return (
                  <Link
                    key={relatedTool.id}
                    to={`/tool/${relatedSlug}`}
                    className="
                      p-4 rounded-xl
                      bg-neutral-100 dark:bg-neutral-900
                      border border-neutral-200 dark:border-neutral-800
                      hover:border-lime-500 dark:hover:border-lime-500
                      transition-all hover:shadow-lg
                    "
                  >
                    <div className="flex items-center gap-3">
                      {/* SEO: Alt text via aria-label for icons */}
                      <span 
                        className="text-2xl" 
                        role="img" 
                        aria-label={`${relatedTool.name} icon`}
                      >
                        {relatedTool.icon || "🧰"}
                      </span>
                      <div>
                        <h3 className="font-medium text-neutral-900 dark:text-white">
                          {relatedTool.name}
                        </h3>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 line-clamp-1">
                          {relatedTool.description || "Explore this tool"}
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            {/* SEO: Link to full category for more internal linking */}
            <div className="mt-4 text-center">
              <Link 
                to={`/?category=${encodeURIComponent(tool.category)}`}
                className="text-lime-600 dark:text-lime-400 hover:underline text-sm font-medium"
              >
                View all {getCategoryDisplayName(tool.category)} →
              </Link>
            </div>
          </section>
        )}

        {/* SEO: Trust & Authority Signals */}
        <section className="mt-12 p-4 rounded-xl bg-neutral-50 dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-800">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-neutral-500 dark:text-neutral-400">
            <span className="flex items-center gap-1">
              ✓ Curated manually
            </span>
            <span className="flex items-center gap-1">
              ✓ No pirated tools
            </span>
            <span className="flex items-center gap-1">
              ✓ Updated regularly
            </span>
          </div>
        </section>

        {/* SEO: Submit Tool CTA for organic growth */}
        <section className="mt-12 text-center p-6 rounded-2xl bg-gradient-to-br from-neutral-100 to-neutral-50 dark:from-neutral-900 dark:to-neutral-800 border border-neutral-200 dark:border-neutral-700">
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-2">
            Know a similar tool we should add?
          </p>
          <Link
            to="/suggest"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-lime-400 to-emerald-500 text-black font-semibold text-sm hover:shadow-lg transition-all"
          >
            📝 Submit a Tool
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}
